import type { QuoteScreenPrintResult, ResolvedGarmentSummary } from '@fsp/contracts';
import { Check, Clock, Copy, Info, Receipt, TriangleAlert, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  clipboardSummary,
  currency,
  formatError,
  MARGIN_THRESHOLDS,
  marginTone,
  type QuoteError,
  type UiValues,
} from './_shared';
import { AskAiDrawer } from './ask-ai-drawer';
import { dispatchOpenRecovery, dispatchOpenSuggest } from './calc-assist-events';
import type { PinnedScenario } from './calc-pinned';
import { type CalcRecent, clearCalcRecents, formatAge, loadCalcRecents } from './calc-recents';
import type { LiveQuoteState } from './use-live-quote';

type ResultPanelProps = {
  state: LiveQuoteState;
  values: UiValues;
  form: UseFormReturn<UiValues>;
  /** Pin management callback — surfaces the new list to the page chrome. */
  onPinnedChange?: (list: PinnedScenario[]) => void;
};

function rushBadgeText(rush: UiValues['rush']): string | null {
  if (rush === 'rush') return 'Rush · +25%';
  if (rush === 'hot_rush') return 'Hot · +50%';
  return null;
}

export function QuoteResultPanel({ state, values, form, onPinnedChange }: ResultPanelProps) {
  if (state.status === 'empty')
    return (
      <div className="space-y-3">
        <EmptyState />
        <PreReadyActionBar form={form} values={values} />
      </div>
    );
  if (state.status === 'error')
    return (
      <div className="space-y-3">
        <ErrorState error={state.error} values={values} />
        <PreReadyActionBar form={form} values={values} />
      </div>
    );
  const result = state.status === 'ready' ? state.result : null;
  const resolvedGarment = state.status === 'ready' ? state.resolvedGarment : undefined;
  const loading = state.status === 'calculating';

  const rushBadge = rushBadgeText(values.rush);

  return (
    <div className="space-y-5 rounded-xl border bg-card p-4 shadow-sm md:space-y-6 md:p-6">
      <Header
        result={result}
        loading={loading}
        resolvedGarment={resolvedGarment}
        rushBadge={rushBadge}
      />
      {result && !loading && <FloorChip result={result} />}
      <Divider />
      <MarginRow result={result} loading={loading} />
      <Divider />
      <StatsList result={result} loading={loading} />
      {result && <Breakdown result={result} />}
      {result && (
        <ActionBar values={values} result={result} form={form} onPinnedChange={onPinnedChange} />
      )}
    </div>
  );
}

/**
 * Action row shown under the Empty and Error states. Mounts the
 * AskAiDrawer and the Recent popover so their cross-component
 * affordances (e.g., the parse textbox's open-suggest event) stay
 * reachable even when the live quote isn't ready. Pin is omitted
 * here because there's no ready result to pin.
 */
function PreReadyActionBar({ form, values }: { form: UseFormReturn<UiValues>; values: UiValues }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <AskAiDrawer form={form} values={values} />
      <RecentButton form={form} />
    </div>
  );
}

function Header({
  result,
  loading,
  resolvedGarment,
  rushBadge,
}: {
  result: QuoteScreenPrintResult | null;
  loading: boolean;
  resolvedGarment?: ResolvedGarmentSummary;
  rushBadge: string | null;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        {loading ? (
          <Skeleton className="h-10 w-32" />
        ) : (
          <span className="text-4xl font-semibold tracking-tight tabular-nums">
            {currency(result?.perUnitSell)}
          </span>
        )}
        <span className="text-sm text-muted-foreground">/ unit</span>
        {rushBadge && !loading && (
          <span className="ml-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
            {rushBadge}
          </span>
        )}
      </div>
      <div className="text-sm text-muted-foreground tabular-nums">
        {loading ? (
          <Skeleton className="h-4 w-56" />
        ) : result ? (
          <>
            {result.totalQty} × = {currency(result.orderTotal)}
            <span className="px-1.5 text-muted-foreground/60">·</span>
            <span>cost {currency(result.perUnitCost)}/ea</span>
          </>
        ) : null}
      </div>
      {resolvedGarment && !loading && (
        <p className="pt-1 text-[11px] text-muted-foreground">
          Resolved at SS:{' '}
          <span className="font-medium text-foreground">{resolvedGarment.style}</span>
          {' · '}
          <span className="font-medium text-foreground">{resolvedGarment.colorName}</span>
          {resolvedGarment.colorFamily &&
            resolvedGarment.colorFamily !== resolvedGarment.colorName && (
              <span className="text-muted-foreground"> ({resolvedGarment.colorFamily})</span>
            )}
          {resolvedGarment.sku && (
            <span className="ml-1 font-mono text-[10px] text-muted-foreground/70">
              {resolvedGarment.sku}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function MarginRow({
  result,
  loading,
}: {
  result: QuoteScreenPrintResult | null;
  loading: boolean;
}) {
  if (loading || !result) {
    return (
      <div className="space-y-2.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Margin
          </span>
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-2.5 w-full rounded-full" />
      </div>
    );
  }
  const pct = result.marginPct;
  const tone = marginTone(pct);
  const toneBar =
    tone === 'positive'
      ? 'bg-emerald-500'
      : tone === 'negative'
        ? 'bg-destructive'
        : 'bg-amber-500';
  const toneText =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'negative'
        ? 'text-destructive'
        : 'text-amber-600';
  // Plain-English health word for fast scan. the owner cares about margin
  // health more than decimals; the word conveys "is this a good job?"
  // before the number is even read.
  const healthWord = tone === 'positive' ? 'Strong' : tone === 'negative' ? 'Tight' : 'Healthy';
  const clamped = Math.max(0, Math.min(100, pct));

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Margin
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Margin thresholds"
                  className="text-muted-foreground/60 hover:text-muted-foreground"
                >
                  <Info className="size-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-[11px] tabular-nums">
                Tight &lt; {MARGIN_THRESHOLDS.poor}% · Healthy {MARGIN_THRESHOLDS.poor}–
                {MARGIN_THRESHOLDS.ok}% · Strong ≥ {MARGIN_THRESHOLDS.ok}%
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <span className="flex items-baseline gap-2">
          <span className={`text-xs font-medium uppercase tracking-wide ${toneText}`}>
            {healthWord}
          </span>
          <span className={`text-xl font-semibold tabular-nums ${toneText}`}>
            {pct.toFixed(1)}%
          </span>
        </span>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`absolute inset-y-0 left-0 ${toneBar} transition-[width] duration-300`}
          style={{ width: `${clamped}%` }}
        />
        <div
          className="absolute inset-y-0 w-px bg-background/60"
          style={{ left: `${MARGIN_THRESHOLDS.poor}%` }}
          aria-hidden
        />
        <div
          className="absolute inset-y-0 w-px bg-background/60"
          style={{ left: `${MARGIN_THRESHOLDS.ok}%` }}
          aria-hidden
        />
      </div>
      {pct < MARGIN_THRESHOLDS.ok && <RecoveryBanner />}
    </div>
  );
}

/**
 * Passive CTA under the margin gauge. Fires when live margin drops
 * below the healthy threshold, pointing the user at the parallel
 * recovery-options search without them having to remember to open
 * Ask AI. No LLM involvement — recovery is pure engine math.
 */
function RecoveryBanner() {
  return (
    <button
      type="button"
      onClick={() => dispatchOpenRecovery()}
      className="mt-2 flex w-full items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-left text-xs transition-colors hover:bg-primary/10"
    >
      <span className="flex items-center gap-2">
        <Wrench className="size-3.5 text-primary" />
        <span>
          <span className="font-medium">Margin tight — see recovery options</span>
          <span className="ml-2 text-muted-foreground">
            Runs qty, print, discount, garment-swap levers.
          </span>
        </span>
      </span>
      <span className="text-primary">Open →</span>
    </button>
  );
}

function StatsList({
  result,
  loading,
}: {
  result: QuoteScreenPrintResult | null;
  loading: boolean;
}) {
  return (
    <dl className="space-y-2 text-sm">
      <StatRow label="Setup fee" loading={loading}>
        {result && <span className="tabular-nums">{currency(result.setupFee)}</span>}
      </StatRow>
    </dl>
  );
}

function StatRow({
  label,
  loading,
  children,
}: {
  label: string;
  loading: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{loading ? <Skeleton className="h-4 w-16" /> : children}</dd>
    </div>
  );
}

/**
 * Escalating floor indicator. Replaces the small FloorValue that used
 * to live in the stats list. Three states tied to `sell - floor`:
 *
 *   breach  (< 0)      — red dominant: red border + 2px box-shadow, value at 22px.
 *   close   (< $0.50)  — amber: warm border + tint, value at 20px.
 *   safe    (else)     — emerald: quiet confirm, value at 16px.
 *
 * Sits directly under price per the Claude Design handoff
 * (`Calculator proposals.html` → "Floor indicator · 3 escalation
 * states"). Was row 2 of a stats list before; the spec called for it
 * to dominate when close/breach so the owner sees a problem before sending.
 */
function FloorChip({ result }: { result: QuoteScreenPrintResult }) {
  const delta = result.perUnitSell - result.floorPerUnit;
  const state = delta < 0 ? 'breach' : delta < 0.5 ? 'close' : 'safe';

  const wrap =
    state === 'breach'
      ? 'border-destructive bg-destructive/10 shadow-[0_0_0_2px_hsl(var(--destructive)/0.2)]'
      : state === 'close'
        ? 'border-amber-500/40 bg-amber-500/10'
        : 'border-border bg-emerald-500/5';
  const head =
    state === 'breach'
      ? 'text-destructive'
      : state === 'close'
        ? 'text-amber-600'
        : 'text-emerald-600';
  const valueSize =
    state === 'breach' ? 'text-[22px]' : state === 'close' ? 'text-[20px]' : 'text-base';
  const deltaTone =
    state === 'breach'
      ? 'text-destructive font-semibold'
      : state === 'close'
        ? 'text-amber-600'
        : 'text-emerald-600';

  const headText =
    state === 'breach' ? 'Below floor' : state === 'close' ? 'Close to floor' : 'Floor safe';
  const Icon = state === 'safe' ? Check : TriangleAlert;
  const formattedDelta = `${delta >= 0 ? '+' : '−'}${currency(Math.abs(delta))}`;

  return (
    <div className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2.5 ${wrap}`}>
      <div
        className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${head}`}
      >
        <Icon className="size-3.5" />
        {headText}
      </div>
      <div className="flex items-baseline gap-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Floor
        </span>
        <span
          className={`font-semibold tabular-nums ${valueSize} ${state === 'breach' ? 'text-destructive' : 'text-foreground'}`}
        >
          {currency(result.floorPerUnit)}
        </span>
        <span className={`ml-auto text-xs tabular-nums ${deltaTone}`}>{formattedDelta}</span>
      </div>
    </div>
  );
}

function Breakdown({ result }: { result: QuoteScreenPrintResult }) {
  return (
    <details className="group border-t pt-4 text-sm">
      <summary className="-m-1 cursor-pointer list-none rounded p-1 text-muted-foreground hover:text-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="transition-transform group-open:rotate-90">▸</span>
          Full breakdown
        </span>
      </summary>
      <dl className="mt-3 space-y-1">
        <BreakdownRow label="Blank / unit" value={currency(result.breakdown.blankCostPerUnit)} />
        <BreakdownRow
          label="Size upcharge (total)"
          value={currency(result.breakdown.sizeUpchargeTotal)}
        />
        <BreakdownRow label="Print rate" value={currency(result.breakdown.printRate)} />
        <BreakdownRow
          label="Extra color charge"
          value={currency(result.breakdown.extraColorCharge)}
        />
        <BreakdownRow
          label="Extra location charge"
          value={currency(result.breakdown.extraLocationCharge)}
        />
        <BreakdownRow
          label="Setup amortised / unit"
          value={currency(result.breakdown.setupAmortizedPerUnit)}
        />
        <BreakdownRow label="Discount / unit" value={currency(result.breakdown.discountPerUnit)} />
        <BreakdownRow
          label="Dark underbase / unit"
          value={currency(result.breakdown.darkUnderbasePerUnit)}
        />
      </dl>
    </details>
  );
}

function BreakdownRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-border/40 py-1 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-xs tabular-nums">{value}</dd>
    </div>
  );
}

function Divider() {
  return <div className="border-t border-border/60" />;
}

function EmptyState() {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/20 p-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-full border bg-background text-muted-foreground">
        <Receipt className="size-5" />
      </div>
      <div>
        <p className="text-sm font-medium">Enter quantities to see pricing</p>
        <p className="mt-1 text-xs text-muted-foreground">Results update live as you type.</p>
      </div>
    </div>
  );
}

function ErrorState({ error, values }: { error: QuoteError; values: UiValues }) {
  const { title, message } = formatError(error);
  // When SS can't find the style the owner typed ("Gildan hoodie", "Bella tee"),
  // offer a one-click "Ask AI to pick one" instead of leaving a dead end.
  // Category is guessed from the typed style text so the suggestion engine
  // returns the right shape.
  const isSsNotFound = error.body?.type === 'not_found' || error.body?.type === 'color_not_found';
  const guessedCategory = guessCategoryFromStyle(values.style);
  return (
    <Alert variant="destructive" className="rounded-xl">
      <TriangleAlert className="size-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{message}</p>
        {isSsNotFound && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => dispatchOpenSuggest({ category: guessedCategory })}
          >
            Ask AI to pick one
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function guessCategoryFromStyle(style: string | undefined): 'tee' | 'hoodie' | undefined {
  if (!style) return undefined;
  const s = style.toLowerCase();
  if (/\b(hoodie|hoody|sweatshirt|pullover|zip-?up)\b/.test(s)) return 'hoodie';
  if (/\b(tee|t-?shirt|shirt|short[-\s]?sleeve)\b/.test(s)) return 'tee';
  return undefined;
}

// ─── Action bar (Copy + Recent) ─────────────────────────────────────────────

/**
 * Footer actions rendered beneath the breakdown when a quote is ready.
 * Copy puts a one-liner on the clipboard (for texting / Slacking the
 * customer). Recent opens a popover to restore a prior calc into the
 * form — primary affordance for the "what if I change X?" JTBD.
 */
/**
 * Proposal C: Pin moved up to the scenario tabs row (every tab IS a
 * scenario). The action bar now only carries Copy + Ask AI + Recent.
 * `onPinnedChange` stays in the prop contract because the outer page
 * still hydrates/restores pins, but the PinButton is retired here.
 */
function ActionBar({
  values,
  result,
  form,
}: {
  values: UiValues;
  result: QuoteScreenPrintResult;
  form: UseFormReturn<UiValues>;
  onPinnedChange?: (list: PinnedScenario[]) => void;
}) {
  // Mobile (<md): 3 equal-width buttons in a row so each is tappable;
  // desktop (md+): natural size flex-row with the two ghost buttons
  // clustered on the right (matches the prior layout).
  return (
    <div className="grid grid-cols-3 gap-2 border-t pt-4 md:flex md:flex-wrap md:items-center md:justify-between">
      <CopyButton values={values} result={result} className="w-full md:w-auto" />
      <AskAiDrawer form={form} values={values} className="w-full md:w-auto" />
      <RecentButton form={form} className="w-full md:w-auto" />
    </div>
  );
}

function CopyButton({
  values,
  result,
  className,
}: {
  values: UiValues;
  result: QuoteScreenPrintResult;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  // Reset the "Copied" flash after 1.5s so repeat copies animate again.
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function handleCopy() {
    const text = clipboardSummary(values, {
      perUnitSell: result.perUnitSell,
      orderTotal: result.orderTotal,
      marginPct: result.marginPct,
      mode: result.mode,
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions / older browser). Silent fail;
      // the customer-facing number is still visible on screen.
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className={`gap-1.5 ${className ?? ''}`}
    >
      {copied ? (
        <>
          <Check className="size-3.5 text-emerald-600" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="size-3.5" />
          <span>Copy totals</span>
        </>
      )}
    </Button>
  );
}

function RecentButton({ form, className }: { form: UseFormReturn<UiValues>; className?: string }) {
  return (
    <RecentPopover form={form}>
      <Button type="button" variant="ghost" size="sm" className={`gap-1.5 ${className ?? ''}`}>
        <Clock className="size-3.5" />
        <span>Recent</span>
      </Button>
    </RecentPopover>
  );
}

function RecentPopover({
  form,
  children,
}: {
  form: UseFormReturn<UiValues>;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CalcRecent[]>([]);

  // Reload on every open so edits to the history (clear, new calcs in
  // another tab) are reflected without a full page refresh.
  useEffect(() => {
    if (open) setItems(loadCalcRecents());
  }, [open]);

  function handleRestore(entry: CalcRecent) {
    form.reset(entry.values, { keepDefaultValues: true });
    setOpen(false);
  }

  function handleClear() {
    clearCalcRecents();
    setItems([]);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Recent calculations
          </span>
          {items.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            Your most recent 10 calculations will appear here.
          </div>
        ) : (
          <ul className="max-h-[360px] overflow-y-auto py-1">
            {items.map((entry) => (
              <li key={entry.at}>
                <button
                  type="button"
                  onClick={() => handleRestore(entry)}
                  className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted/60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{entry.summary.label}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
                      <span>{currency(entry.summary.perUnitSell)}/ea</span>
                      <span>·</span>
                      <span>{currency(entry.summary.orderTotal)}</span>
                      <span>·</span>
                      <span className={toneClass(entry.summary.marginPct)}>
                        {entry.summary.marginPct.toFixed(0)}%
                      </span>
                      <span>·</span>
                      <span className="uppercase">{entry.summary.mode}</span>
                    </div>
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {formatAge(entry.at)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function toneClass(marginPct: number): string {
  const tone = marginTone(marginPct);
  if (tone === 'positive') return 'text-emerald-600';
  if (tone === 'negative') return 'text-destructive';
  return 'text-amber-600';
}
